import { useEffect, useRef } from "react";
import { useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Overline, Reveal } from "@/components/Reveal";

const FINISHED = [
  {
    src: "/assets/work-e.jpg",
    alt: "Finished master bathroom remodel with frameless glass shower and freestanding tub by Gregory Tile",
    label: "Master Bath",
  },
  {
    src: "/assets/work-d.jpg",
    alt: "Finished walk-in tile shower with bench seat and hexagon floor by Gregory Tile",
    label: "Walk-In Shower",
  },
];

const BeforeAfter = () => {
  const videoRef = useRef(null);
  const inView = useInView(videoRef, { amount: 0.35 });

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (inView) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [inView]);

  return (
    <section className="bg-navy py-16 md:py-24" data-testid="before-after-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:px-10">
        <Reveal>
          <div className="relative">
            <div className="absolute -inset-3 rounded-[2rem] border-2 border-salmon/40" aria-hidden="true" />
            <video
              ref={videoRef}
              muted
              loop
              playsInline
              preload="metadata"
              poster="/assets/work-a.webp"
              data-testid="before-after-video"
              className="relative mx-auto max-h-[70vh] w-auto max-w-full rounded-[1.75rem] bg-navy-deep shadow-2xl"
            >
              <source src="/assets/before-after.webm" type="video/webm" />
              <source src="/assets/before-after.mp4" type="video/mp4" />
            </video>
            <span className="absolute left-4 top-4 rounded-full bg-salmon px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white shadow-lg">
              Before & After
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <Overline light>Watch the Transformation</Overline>
          <h2 className="mt-3 font-serif text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            From Builder-Grade to Beautiful
          </h2>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-white/70">
            This is what a Gregory Tile remodel looks like — the old shower comes out, and a hand-set tile walk-in goes in. No filters, no staging: just Diego's work, start to finish.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-4">
            {FINISHED.map((f) => (
              <figure key={f.label} className="group relative overflow-hidden rounded-2xl">
                <img
                  src={f.src}
                  alt={f.alt}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy-deep/95 to-transparent p-3 pt-10">
                  <p className="text-sm font-bold text-white">{f.label}</p>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-salmon-vibrant">
                    Finished Result
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>

          <Link
            to="/bathroom-remodeling"
            data-testid="before-after-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-salmon px-6 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-salmon-dark hover:shadow-lg"
          >
            See Bathroom Remodels <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default BeforeAfter;
